import {
  ButtonIcon,
  Cell,
  type ColumnConfig,
  Flex,
  Link,
  Table,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import { ProgressCircle } from '@internal/ui-common';
import {
  type TrackTrendDto,
  type TeamTrackSummaryDto,
} from '@internal/scorecards-common';
import { useMemo } from 'react';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { ownerToEntityRef } from '../../utils/ownerToEntityRef';
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  YAxis,
} from 'recharts';
import { RiInformationLine } from '@remixicon/react';

import { PassFailBar } from '../SegmentedBar/SegmentedBar';
import { useTeamTrackTrends } from '../../hooks/useTeamTrackTrends';
import styles from './TracksTable.module.css';

const UNTRACKED_ROW_ID = 'uncategorized';

interface TrackRow {
  id: string;
  track: TeamTrackSummaryDto;
}

interface TracksTableProps {
  team: string;
  tracks: TeamTrackSummaryDto[];
  projectsTotal: number;
  selectedCategoryIds?: Set<string>;
  onCategoryClick?: (id: string) => void;
}

export const TracksTable = ({
  team,
  tracks,
  projectsTotal,
  selectedCategoryIds,
  onCategoryClick,
}: TracksTableProps) => {
  const { loading: trendsLoading, trends } = useTeamTrackTrends(team);
  const rows: TrackRow[] = useMemo(
    () =>
      tracks.map(track => ({
        id: track.id ?? UNTRACKED_ROW_ID,
        track,
      })),
    [tracks],
  );

  const trendMap = useMemo<Map<string | null, TrackTrendDto>>(
    () => new Map((trends ?? []).map(t => [t.trackId, t])),
    [trends],
  );

  const columnConfig: ColumnConfig<TrackRow>[] = useMemo(
    () => [
      {
        id: 'track',
        label: 'Track',
        isRowHeader: true,
        cell: ({ track }) => (
          <Cell>
            <Flex direction="column" gap="1">
              {onCategoryClick && track.id !== null ? (
                <button
                  type="button"
                  className={`${styles.categoryName} ${styles.categoryButton}${
                    selectedCategoryIds?.has(track.id!)
                      ? ` ${styles.categoryButtonActive}`
                      : ''
                  }`}
                  onClick={() => onCategoryClick(track.id!)}
                >
                  {track.name}
                </button>
              ) : (
                <Text as="p" className={styles.categoryName}>
                  {track.name}
                </Text>
              )}
              {track.description && (
                <Text
                  as="p"
                  variant="body-small"
                  color="secondary"
                  className={styles.categoryDescription}
                >
                  {track.description}
                </Text>
              )}
              {track.docsUrl && (
                <Link
                  href={track.docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body-small"
                >
                  Track documentation
                </Link>
              )}
            </Flex>
          </Cell>
        ),
      },
      {
        id: 'owner',
        label: 'Owner',
        maxWidth: '15%',
        cell: ({ track }) => {
          if (!track.owner) {
            return (
              <Cell>
                <Text as="p" color="secondary">
                  -
                </Text>
              </Cell>
            );
          }
          return (
            <Cell>
              <EntityRefLink entityRef={ownerToEntityRef(track.owner)} />
            </Cell>
          );
        },
      },
      {
        id: 'projects',
        label: 'Projects',
        cell: ({ track }) => {
          const failingTotal = Object.values(track.failingBySeverity).reduce(
            (s, n) => s + n,
            0,
          );
          const passing = track.inScope - failingTotal;
          return (
            <Cell>
              <Flex direction="column" gap="2">
                <Text as="p">{failingTotal} failing</Text>
                <Text as="p" variant="body-small" color="secondary">
                  {track.inScope} of {projectsTotal} projects tracked
                </Text>
                <PassFailBar
                  passing={passing}
                  failing={failingTotal}
                  total={track.inScope}
                />
              </Flex>
            </Cell>
          );
        },
      },
      {
        id: 'progress',
        label: 'Progress %',
        maxWidth: '10%',
        cell: ({ track }) => (
          <Cell>
            <ProgressCircle
              value={track.progress}
              size={48}
              color="var(--zf-sc-progress)"
              colorEnd="var(--zf-sc-progress-end)"
              aria-label={
                track.progress !== null
                  ? `Progress: ${track.progress}/100`
                  : 'Progress: N/A'
              }
            />
          </Cell>
        ),
      },
      {
        id: 'trend',
        label: '12-Weeks Trend',
        cell: ({ track }) => {
          const trend = trendMap.get(track.id ?? null);
          const gradientId = `trend-gradient-${track.id ?? 'null'}`;

          if (trendsLoading) {
            return (
              <Cell>
                <ProgressCircle aria-label="Loading trend" />
              </Cell>
            );
          }

          if (!trend?.weeks.length) {
            return (
              <Cell>
                <span>—</span>
              </Cell>
            );
          }

          return (
            <Cell>
              <ResponsiveContainer width="100%" height={40}>
                <LineChart data={trend.weeks}>
                  <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="var(--zf-sc-progress)" />
                      <stop
                        offset="100%"
                        stopColor="var(--zf-sc-progress-end)"
                      />
                    </linearGradient>
                  </defs>
                  <YAxis domain={[0, 100]} hide />
                  <Line
                    type="monotone"
                    dataKey="progress"
                    stroke={`url(#${gradientId})`}
                    strokeWidth={1.5}
                    dot={false}
                    isAnimationActive={false}
                  />
                  <RechartsTooltip
                    formatter={(v: number) => [`${v}%`, 'Progress']}
                    labelFormatter={(label: string) => `Week of ${label}`}
                    contentStyle={{
                      background: 'var(--bui-bg-neutral-1)',
                      border: '1px solid var(--bui-border-1)',
                      borderRadius: 4,
                      color: 'var(--bui-fg-primary)',
                      fontSize: 11,
                    }}
                    labelStyle={{ color: 'var(--bui-fg-secondary)' }}
                    itemStyle={{ color: 'var(--bui-fg-primary)' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Cell>
          );
        },
      },
    ],
    [
      projectsTotal,
      selectedCategoryIds,
      onCategoryClick,
      trendsLoading,
      trendMap,
    ],
  );

  return (
    <section aria-labelledby="team-tracks-heading">
      <Flex direction="column" gap="4">
        <Flex justify="between" align="start">
          <Flex direction="column" gap="1">
            <Text as="h2" id="team-tracks-heading" variant="title-small">
              Tracks
            </Text>
            <Flex align="center" gap="1">
              <Text as="p" variant="body-small" color="secondary">
                Compliance and project failure breakdown per track.
              </Text>
              <TooltipTrigger>
                <ButtonIcon
                  aria-label="About Progress %"
                  variant="tertiary"
                  size="small"
                  icon={<RiInformationLine />}
                  className={styles.infoIcon}
                />
                <Tooltip>
                  Progress % is based on unique failing checks across all
                  projects for each track
                </Tooltip>
              </TooltipTrigger>
            </Flex>
          </Flex>
        </Flex>
        <Table
          columnConfig={columnConfig}
          data={rows}
          pagination={{ type: 'none' }}
          emptyState={
            <Text as="p" color="secondary">
              No tracks with results for this team.
            </Text>
          }
        />
      </Flex>
    </section>
  );
};
